import {
  BaSeGrid,
  BaSeGridItem,
  BaSeIcon,
  BaSeParagraph,
  useDebounce,
} from "@base/react";
import AccordionModal from "../../../../components/accordion-modal";
import shirtLogo from "../../../../assets/images/icons/shirt.svg";
import scissorsLogo from "../../../../assets/images/icons/scissors.svg";
import { useEffect, useState } from "react";
import {
  convertObjectValuesToFloat,
  formatCurrency,
  formatFloat,
  sortedArrayByObjectKeys,
  sumValuesPercent,
} from "../../../../services/utils";
import InputNumber from "../../../../components/form-builder/input-number";
import InputPercentage from "../../../../components/form-builder/input-percentage";
import { validatePercentage } from "../../../../services/validate";
import { hasDuplicates, validateInventoryTurnover } from "../functions";

const InventoryTurnover = ({
  data,
  isDisabled,
  onChange,
  onInventoryBelowThreshold,
  showAccordion,
}) => {
  const [inventory, setInventory] = useState([]);
  const debouncedInventory = useDebounce(inventory, 1000);
  const [hasRepeats, setHasRepeats] = useState(false);
  const [actionAddField, setActionAddField] = useState(false);
  const [currentField, setCurrentField] = useState(data.length);
  const [lastTotal, setLastTotal] = useState(null);

  const handleChange = (order, name, newValue) => {
    let value = newValue;
    if (name === "days") {
      value = value === undefined || value === null ? "" : value;
    }

    const updatedForm = inventory.map((field) =>
      field.order === order ? { ...field, [name]: value } : field
    );

    setInventory(updatedForm);

    setHasRepeats(hasDuplicates(updatedForm));
  };
  const addField = () => {
    setInventory((prevFields) => [
      ...prevFields,
      {
        order:
          prevFields.length > 0
            ? prevFields[prevFields.length - 1].order + 1
            : 1,
        days: "",
        value: 0,
      },
    ]);
    setActionAddField(true);
    setCurrentField((prev) => prev + 1);
  };

  const removeField = (orderToRemove) => {
    setInventory((prevFields) =>
      prevFields
        .filter((field) => field.order !== orderToRemove)
        .map((field, index) => ({
          ...field,
          order: index + 1,
        }))
    );
    setCurrentField((prev) => prev - 1);
  };

  useEffect(() => {
    if (data.length) {
      const sorted = sortedArrayByObjectKeys(data);
      setInventory(sorted);
    }
  }, [data]);
  useEffect(() => {
    if (!actionAddField && validateInventoryTurnover(debouncedInventory)) {
      onChange(debouncedInventory);
    } else {
      setActionAddField(false);
    }
    // eslint-disable-next-line
  }, [debouncedInventory]);

  useEffect(() => {
    const total = sumValuesPercent(convertObjectValuesToFloat(inventory));

    if (total !== lastTotal) {
      if (showAccordion) {
        showAccordion(total === 100);
      }

      if (onInventoryBelowThreshold) {
        onInventoryBelowThreshold(total < 100, total);
      }

      setLastTotal(total);
    }
  }, [inventory, currentField, showAccordion, onInventoryBelowThreshold, lastTotal]);

  return (
    <div className="step inventory-turnover">
      <div className="step--info">
        <h3 className="step--title">
          Indica o tempo médio que as mercadorias permanecem no seu estoque.
          Você pode estimar esse valor pelo intervalo entre cada compra de
          mercadoria: <br />
          <br />
          i. Compras diárias: estoque fica 1 dia parado
          <br />
          ii. Compras semanais: estoque fica, em média, 3,5 dias parado
          <br />
          iii. Compras mensais: estoque fica, em média, 15 dias parado
          <br />
          <br />
          Caso ainda não tenha essas informações, insira uma média geral do
          tempo que as mercadorias ficam no estoque, e no campo "% das compras",
          preencha com 100%.
          <br />
          <br />
        </h3>
        <h5 className="step--subtitle">
          Os campos são opcionais, mas para garantir resultados mais precisos,
          preencha todas as informações disponíveis.
        </h5>
      </div>
      <AccordionModal title="Exemplos:">
        <div className="example first">
          <img src={shirtLogo} alt="ícone de televisão" />
          <div>
            <BaSeParagraph color="#000">
              <span>Caso 1:</span> Loja de Eletrônicos
            </BaSeParagraph>
            <ul className="example-list">
              <li>
                Custo Total das Mercadorias Vendidas (CMV): R$ 120.000 o Valor
                Médio do Estoque: R$ 30.000
              </li>
              <li>
                <strong>Giro de Estoque</strong> = 120.000 / 30.000 ={" "}
                <strong>4 vezes ao ano</strong>
              </li>
              <li>
                <strong>Tempo Médio de Permanência</strong> = 360 / 4 ={" "}
                <strong>90 dias</strong>
              </li>
            </ul>
          </div>
        </div>
        <div className="example">
          <img src={scissorsLogo} alt="ícone de talheres" />
          <div>
            <BaSeParagraph color="#000">
              <span>Caso 2:</span> Mercado de Alimentos
            </BaSeParagraph>
            <ul className="example-list">
              <li>
                Custo Total das Mercadorias Vendidas (CMV): R$ 500.000 o Valor
                Médio do Estoque: R$ 100.000
              </li>
              <li>
                <strong>Giro de Estoque</strong> = 500.000 / 100.000 ={" "}
                <strong>5 vezes ao ano</strong>
              </li>
              <li>
                <strong>Tempo Médio de Permanência</strong> = 360 / 5 ={" "}
                <strong>72 dias</strong>
              </li>
            </ul>
          </div>
        </div>
      </AccordionModal>

      <div className="inventory-turnover__wrapper">
        {inventory.map(({ order, days, value }) => {
          return (
            <div key={order} className="grid-buttons-area">
              <BaSeGrid>
                <BaSeGridItem size={4}>
                  <InputNumber
                    isDisabled={isDisabled}
                    maxLength={3}
                    maxValue={500}
                    inputStatus={
                      formatFloat(value) > 0 && !isDisabled
                        ? hasRepeats
                          ? "invalid"
                          : "valid"
                        : ""
                    }
                    value={days}
                    onFnExternalChange={(e) => {
                      handleChange(order, "days", e);
                    }}
                    placeholder=""
                    label="Dias"
                  />
                </BaSeGridItem>
                <BaSeGridItem size={4}>
                  <div className="top-input">
                    <InputPercentage
                      isDisabled={isDisabled}
                      prefix="% "
                      maxLength={8}
                      inputStatus={
                        (Number(days) > 0 && formatFloat(value) === 0) ||
                        validatePercentage(value)
                          ? "invalid"
                          : ""
                      }
                      value={value}
                      onFnExternalChange={(e) => {
                        handleChange(order, "value", e);
                      }}
                      placeholder="%"
                      label="% das compras"
                    />
                  </div>
                </BaSeGridItem>
                <BaSeGridItem size={2}>
                  <div className="remove-button">
                    {!isDisabled && (
                      <button
                        type="button"
                        className="btn-without-style btn-add-more"
                        onClick={() => removeField(order)}
                        disabled={currentField === 1}
                        style={{
                          cursor: currentField !== 1 ? "pointer" : "no-drop",
                        }}
                      >
                        <BaSeIcon
                          color={currentField !== 1 ? "#005EB8" : "#C0C0C0"}
                          description="Adicionar"
                          name="minus-square"
                          size={24}
                        />
                      </button>
                    )}
                  </div>
                </BaSeGridItem>
              </BaSeGrid>
            </div>
          );
        })}
        <div className="result">
          <BaSeGrid>
            <BaSeGridItem size={4}>
              {hasRepeats && (
                <p className="error-message">
                  Existem valores duplicados nos campos de{" "}
                  <strong>"Dias"</strong>. Corrija para continuar.
                </p>
              )}
            </BaSeGridItem>
            <BaSeGridItem size={4}>
              <div
                className={`base-input-container ${
                  sumValuesPercent(convertObjectValuesToFloat(inventory)) <= 100
                    ? "input-100"
                    : "input-not-100"
                }`}
              >
                <InputPercentage
                  suffix="%"
                  value={formatCurrency(
                    sumValuesPercent(convertObjectValuesToFloat(inventory))
                  )}
                  placeholder="%"
                  width="100%"
                  isDisabled={true}
                />
              </div>
            </BaSeGridItem>
          </BaSeGrid>
        </div>

        {inventory.length <= 500 && !isDisabled ? (
          <button
            type="button"
            className="btn-without-style btn-add-more"
            onClick={addField}
            disabled={hasRepeats}
          >
            <BaSeIcon
              color="#2E5DE6"
              description="Adicionar"
              name="plus-square"
              size={24}
            />
            <span>Adicionar prazo e percentual</span>
          </button>
        ) : (
          ""
        )}
      </div>
      <span className="text-muted">
        Se tiver mercadorias com tempos diferentes de giro, clique em “
        <span className="blue">+ Adicionar prazo e percentual</span>” e insira
        os valores correspondentes. Lembrando que a soma dos percentuais deve
        bater <strong>100%</strong>.
      </span>
    </div>
  );
};

export default InventoryTurnover;
